import { useMemo } from 'react';

import { resolver } from './ui/resolver';
import { useGlobalTheme } from './useGlobalTheme';

const FALLBACK_COLOR = '#000000ff';
const MAX_VAR_RESOLUTION_DEPTH = 10;

export type CssColorResolver = (cssValue: string) => string;

function findClosingParenthesis(input: string, openParenthesisIdx: number): number {
  let depth = 1;
  for (let i = openParenthesisIdx + 1; i < input.length; i++) {
    if (input[i] === '(') depth++;
    else if (input[i] === ')') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

function splitVarArgs(inside: string): { name: string; fallback: string } {
  let depth = 0;
  for (let i = 0; i < inside.length; i++) {
    if (inside[i] === '(') depth++;
    else if (inside[i] === ')') depth--;
    else if (inside[i] === ',' && depth === 0) {
      return { name: inside.slice(0, i).trim(), fallback: inside.slice(i + 1).trim() };
    }
  }
  return { name: inside.trim(), fallback: '' };
}

function resolveVars(input: string, colorMap: Record<string, string>, depth = 0): string {
  if (depth > MAX_VAR_RESOLUTION_DEPTH) return input;

  let result = '';
  let i = 0;
  while (i < input.length) {
    if (input.startsWith('var(', i)) {
      const closeIdx = findClosingParenthesis(input, i + 3);
      if (closeIdx === -1) {
        result += input.slice(i);
        break;
      }
      const { name, fallback } = splitVarArgs(input.slice(i + 4, closeIdx));
      const mapped = colorMap[name];
      const replacement = mapped !== undefined ? mapped : fallback;
      result += resolveVars(replacement, colorMap, depth + 1);
      i = closeIdx + 1;
    } else {
      result += input[i];
      i++;
    }
  }
  return result;
}

function convertSingleColorToHexa(color: string): string {
  const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
  const rgbaMatch = color.match(/^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)$/);

  if (rgbMatch || rgbaMatch) {
    const match = rgbMatch || rgbaMatch;
    if (!match) return FALLBACK_COLOR;

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);
    const a = match[4] ? Math.round(parseFloat(match[4]) * 255) : 255;

    const toHex = (n: number): string => {
      const hex = n.toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(a)}`;
  }

  if (color.startsWith('#')) {
    let hex = color.slice(1);

    if (hex.length === 3) {
      hex = hex
        .split('')
        .map(char => char + char)
        .join('');
    }

    if (hex.length === 6) {
      hex += 'ff';
    }

    return `#${hex}`;
  }

  return FALLBACK_COLOR;
}

function convertCssValueToHexaColor(cssValue: string, colorMap: Record<string, string>): string {
  const resolved = resolveVars(cssValue, colorMap).trim();
  const withHexaColors = resolved.replace(/rgba?\(\d+,\s*\d+,\s*\d+(?:,\s*[\d.]+)?\)/g, convertSingleColorToHexa);

  const gradientMatch = withHexaColors.match(/(?:linear|radial)-gradient\([^)]+\)/);
  if (gradientMatch) {
    return gradientMatch[0];
  }

  return convertSingleColorToHexa(withHexaColors);
}

/**
 * Hook that returns a CSS-value-to-hex resolver bound to the active Mantine theme
 * (scoped to whichever CSSVarsValuesProvider is the nearest ancestor). Use this in
 * components so theme previews — which mount a second provider with a different
 * theme — resolve colors against the correct subtree.
 */
export function useConvertCssValueToHexaColor(): CssColorResolver {
  const theme = useGlobalTheme();
  return useMemo(() => {
    const colorMap = resolver(theme).variables as Record<string, string>;
    return (cssValue: string) => convertCssValueToHexaColor(cssValue, colorMap);
  }, [theme]);
}
